// src/components/TransportSelect.jsx
// Reusable selector for the transport registry. Renders a searchable
// type-to-filter dropdown (input + datalist — the same convention as the
// customer and design search fields) of active transports. A small URP-style
// "Custom" link sits at the top-right of the field label and reveals free-text
// name + GST textboxes (registered into the transport module on quotation save).
// The parent controls the value via the shape { id, name, gst }:
//   • id   = transport Firestore id ("" = none, "__custom__" = manual)
//   • name = the combined display string stored on customer / quotation
//   • gst  = the transport GST (only really needed for the custom case)
// The optional `label` prop renders the field label with the Custom link on its
// right (the same row treatment as the GST field's "URP" link); `labelStyle`
// lets each form keep its own label typography.
import { useEffect, useId, useState } from "react";
import {
  getActiveTransports, getTransportDisplayName, CUSTOM_TRANSPORT_ID, toTitleCase,
} from "../utils/transport";
import { getGSTValidationMessage } from "../utils/validation";
import { B, fonts } from "./UI";

// Display label of the custom selection in the search field (no longer offered
// as a dropdown option — the "Custom" link on the label row switches to it).
const NONE_LABEL = "None";
const CUSTOM_LABEL = "Custom transport…";

export default function TransportSelect({ transports = [], value, onChange, disabled, compact = false, label, labelStyle }) {
  const listId = useId();
  const activeTransports = getActiveTransports(transports);
  const selectedId = value?.id || "";
  const isCustom = selectedId === CUSTOM_TRANSPORT_ID
    || (Boolean(value?.name) && !activeTransports.some(t => t.id === selectedId));

  const selectedTransport = activeTransports.find(t => t.id === selectedId) || null;
  const selectedLabel = isCustom
    ? CUSTOM_LABEL
    : (selectedTransport ? getTransportDisplayName(selectedTransport) : NONE_LABEL);

  // The custom transport name exactly as typed. The "(GST: …)" suffix is
  // stripped WITHOUT trimming so trailing spaces typed by the user survive the
  // round-trip through the parent's combined `transport_name` string —
  // trimming on every keystroke used to delete spaces as soon as they were
  // typed (e.g. "Patel Transport" could never be entered).
  const customNameValue = (() => {
    const s = String(value?.name || "");
    const g = String(value?.gst || "").trim().toUpperCase();
    const suffix = g ? ` (GST: ${g})` : "";
    return suffix && s.endsWith(suffix) ? s.slice(0, -suffix.length) : s;
  })();
  const customGstValue = String(value?.gst || "").trim().toUpperCase();
  const customGstError = !customGstValue ? "" : getGSTValidationMessage(customGstValue);

  // Search text shown in the field. Re-synced whenever the committed selection
  // changes (customer applied, quotation loaded, an option picked).
  const [query, setQuery] = useState(selectedLabel);
  useEffect(() => { setQuery(selectedLabel); }, [selectedLabel]);

  const inputStyle = {
    background: B.white, border: `1.5px solid ${B.border}`, borderRadius: 8,
    padding: compact ? "9px 12px" : "10px 13px", color: B.text, fontSize: 13,
    outline: "none", width: "100%", boxSizing: "border-box", fontFamily: fonts.body,
  };
  const gstLabelStyle = { display: "block", fontSize: 10, fontWeight: 600, color: B.textMid, marginBottom: 6, letterSpacing: "0.07em", textTransform: "uppercase", fontFamily: fonts.body };

  // Build the combined display string without trimming so a space typed at the
  // end of the name is not dropped before the next character arrives.
  const emitCustom = (name, gst) => onChange({
    id: CUSTOM_TRANSPORT_ID,
    name: gst ? `${name} (GST: ${gst})` : String(name),
    gst,
  });

  // Commit the typed text only when it exactly matches one of the options;
  // partial text stays as the search query (restored on blur).
  const commitQuery = (raw) => {
    if (raw === selectedLabel) return;
    const matchedTransport = activeTransports.find(t => getTransportDisplayName(t) === raw);
    if (matchedTransport) {
      onChange({ id: matchedTransport.id, name: getTransportDisplayName(matchedTransport), gst: matchedTransport.gst || "" });
      return;
    }
    if (raw === NONE_LABEL) { onChange({ id: "", name: "", gst: "" }); }
  };

  const handleQueryChange = (raw) => {
    setQuery(raw);
    commitQuery(raw);
  };

  const handleQueryBlur = () => {
    const isKnownOption = query === NONE_LABEL
      || activeTransports.some(t => getTransportDisplayName(t) === query);
    if (isKnownOption) commitQuery(query);
    else setQuery(selectedLabel); // unmatched search text — restore the selection
  };

  // Small URP-style link that switches to custom mode and reveals the free-text
  // boxes below. Rendered at the top-right of the field label — the same row
  // treatment as the GST field's "URP" link. While custom mode is on, picking
  // "None" or a registry transport in the dropdown dismisses the boxes again.
  const customLink = !disabled && !isCustom ? (
    <button
      type="button"
      onClick={() => emitCustom("", "")}
      style={{ background: "none", border: "none", color: B.orange, textDecoration: "underline", cursor: "pointer", fontSize: compact ? 12 : 11, fontFamily: fonts.body, padding: 0 }}
    >
      Custom
    </button>
  ) : null;

  return (
    <div>
      {label && (
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
          <label style={{ fontSize: 11, fontWeight: 600, color: B.textMid, fontFamily: fonts.body, ...labelStyle }}>{label}</label>
          {customLink}
        </div>
      )}
      {value?.name && !isCustom && (
        <div style={{ fontSize: 11, color: B.green, marginBottom: 6, fontFamily: fonts.body, display: "flex", alignItems: "center", gap: 6 }}>
          <span>✓</span> {selectedLabel}
        </div>
      )}
      <input
        list={listId}
        value={query}
        onChange={e => handleQueryChange(e.target.value)}
        onFocus={e => e.target.select()}
        onBlur={handleQueryBlur}
        disabled={disabled}
        placeholder="Search transport…"
        aria-label="Search transport"
        style={inputStyle}
      />
      <datalist id={listId}>
        <option value={NONE_LABEL} />
        {activeTransports.map(t => (
          <option key={t.id} value={getTransportDisplayName(t)} />
        ))}
      </datalist>
      {/* Fallback placement when no field label was supplied by the parent. */}
      {!label && customLink && <div style={{ marginTop: 6 }}>{customLink}</div>}
      {isCustom && (
        <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 8 }}>
          <input
            value={customNameValue}
            onChange={e => emitCustom(toTitleCase(e.target.value), customGstValue)}
            disabled={disabled}
            placeholder="Transport name"
            style={{ ...inputStyle, borderColor: B.orangeMid }}
          />
          <div>
            <label style={gstLabelStyle}>Transport GST Number (optional)</label>
            <input
              value={customGstValue}
              onChange={e => emitCustom(customNameValue, e.target.value.toUpperCase())}
              disabled={disabled}
              placeholder="e.g. 24ABCDE1234F1Z5"
              style={{ ...inputStyle, borderColor: customGstError ? B.red : B.orangeMid }}
            />
            {customGstError && !disabled && (
              <div style={{ fontSize: 11, color: B.red, marginTop: 4, fontFamily: fonts.body }}>{customGstError}</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
